import {
  Component, inject, signal, PLATFORM_ID, OnInit, computed, effect
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { ThemeService } from 'theme-lib';
import { AuthService } from 'fireauth-lib';
import { BookmarkService } from 'cms-post-lib';
import { ThemeCustomizerService } from '../../services/theme-customizer.service';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [RouterLink, RouterLinkActive],
  templateUrl: './navbar.component.html'
})
export class NavbarComponent implements OnInit {
  private readonly _themeService = inject(ThemeService);
  private readonly _authService = inject(AuthService);
  private readonly _bookmarkService = inject(BookmarkService);
  private readonly _router = inject(Router);
  private readonly _platformId = inject(PLATFORM_ID);
  readonly _customizer = inject(ThemeCustomizerService);

  readonly currentUser = this._authService.currentUser;

  readonly mobileMenuOpen = signal(false);
  readonly userMenuOpen = signal(false);

  /** Bookmark count for badge — hidden when 0 */
  readonly bookmarkCount = computed(() => this._bookmarkService.bookmarks().length);

  constructor() {
    // React to auth state changes:
    // - user logs out → reset bookmark state
    // - user logs in  → load bookmarks to populate badge
    effect(() => {
      const user = this.currentUser();
      if (!user) {
        this._bookmarkService.reset();
      } else {
        // Refresh bookmarks so the badge shows an accurate count after sign-in
        this._bookmarkService.loadBookmarks(environment.orgId).subscribe();
      }
    });
  }

  ngOnInit(): void {
    if (isPlatformBrowser(this._platformId)) {
      // Sync the shared dark signal with the current DOM state
      this._customizer.syncDark();
    }
  }

  toggleTheme(): void {
    this._themeService.toggleTheme();
    // reapplyAccent() calls syncDark() internally, keeping _customizer.dark in sync
    this._customizer.reapplyAccent();
  }

  toggleMobileMenu(): void {
    this.mobileMenuOpen.update(v => !v);
  }

  toggleUserMenu(): void {
    this.userMenuOpen.update(v => !v);
  }

  closeUserMenu(): void {
    this.userMenuOpen.set(false);
  }

  signOut(): void {
    this.closeUserMenu();
    this._authService.logout().subscribe({
      next: () => this._router.navigate(['/'])
    });
  }

  get userInitial(): string {
    const user = this.currentUser();
    if (!user) return '';
    return (user.displayName ?? user.email ?? 'U')[0].toUpperCase();
  }
}
